import App from "next/app";
import Head from "next/head";
import nc from "next-connect";
import Script from "next/script";
import React, { useState } from "react";
import cookieParser from "cookie-parser";
import type { AppProps } from "next/app";
import Router, { useRouter } from "next/router";
import { appWithTranslation } from "next-i18next";

import {
  QueryClient,
  DehydratedState,
  HydrationBoundary,
  QueryClientProvider,
} from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import NextTopLoader from "nextjs-toploader";

import NProgress from "nprogress";

import {
  NotificationsDock,
  ServiceWorkerProvider,
} from "@filadd/phi-plus-layout";
import { Toaster, BreadcrumbProvider } from "@filadd/phi-plus-core";
import { token } from "@filadd/phi-plus-styled-system/tokens";

import { customDehydrate } from "helpers/customDehydrate";

import { Layout } from "components/layout";
import { DevServiceWorkerProvider } from "components/commons";

import {
  AuthProvider,
  getServerSideLoginUrl,
  getServerSideLogoutUrl,
} from "AuthContext";
import authorizationMiddleware from "middlewares/authorization";

import transcodeLocale from "helpers/transcodeLocale";

import {
  fetchLayoutTrinkets,
  useLayoutFeatures,
  generateLayoutTrinketsKey,
} from "hooks/layout";
import { useCourse } from "hooks/courses-new/course";

import type { CustomAppContext } from "types/request";
import type { ExpandedUserMeResponse } from "types/users/me";
import type { CourseResponse } from "types/courses/course";

import "nprogress/nprogress.css";
import "quill/dist/quill.bubble.css";
import "../../styles/globals.css";

const isDev = process.env.NODE_ENV === "development";
const RUDDERSTACK_KEY = `${process.env.RUDDERSTACK_WRITE_KEY}`;
const RUDDERSTACKDATAURL = `${process.env.RUDDERSTACK_DATA_PLANE_URL}`;

Router.events.on("routeChangeStart", () => NProgress.start());
Router.events.on("routeChangeComplete", () => NProgress.done());
Router.events.on("routeChangeError", () => NProgress.done());

const AppServiceWorkerProvider = isDev
  ? DevServiceWorkerProvider
  : ServiceWorkerProvider;

interface DehydratedPageProps {
  dehydratedState?: DehydratedState;
}

type CustomAppProps = AppProps & {
  loginUrl: string;
  logoutUrl: string;
  courseSlug?: string;
  authorizationToken?: string;
  cookies: Record<string, string>;
  userData?: ExpandedUserMeResponse;
  courseData?: CourseResponse;
  initialDehydratedState: DehydratedState;
};

const Application = ({
  cookies,
  loginUrl,
  userData,
  courseData,
  Component,
  pageProps,
  logoutUrl,
  courseSlug,
  authorizationToken,
  initialDehydratedState,
}: CustomAppProps) => {
  const router = useRouter();
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: Infinity,
            meta: {
              locale: router.locale,
              authToken: authorizationToken,
              baseURL: process.env.GATEWAY_URL_CLIENT as string,
            },
          },
          mutations: {
            meta: {
              locale: router.locale,
              authToken: authorizationToken,
              baseURL: process.env.GATEWAY_URL_CLIENT as string,
            },
          },
        },
      }),
  );

  const { dehydratedState } = pageProps as DehydratedPageProps;

  return (
    <React.StrictMode>
      <Head>
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>

      <AuthProvider
        loginUrl={loginUrl}
        logoutUrl={logoutUrl}
        userData={userData}
      >
        <NextTopLoader
          color={token("colors.primary")}
          height={5}
          shadow={false}
          showAtBottom={false}
        />
        <AppServiceWorkerProvider
          configuration={{
            fetch: {
              baseURL: process.env.GATEWAY_URL_CLIENT as string,
              locale: router.locale,
              authToken: authorizationToken,
            },
          }}
        >
          <QueryClientProvider client={queryClient}>
            <HydrationBoundary state={initialDehydratedState}>
              <HydrationBoundary state={dehydratedState}>
                <Script
                  id="rudderstack"
                  strategy="afterInteractive"
                  onReady={() => {
                    if (userData) {
                      const userProperties = {
                        user_id: userData.id,
                        user_short_slug: userData.short_slug,
                        user_email: userData.email,
                        user_name: userData.name,
                        user_lastname: userData.last_name,
                        user_fullname: userData.fullname,
                        url_avatar: userData.url_avatar,
                        phone_number: userData.phone_number,
                      };

                      window.rudderanalytics?.identify(
                        userData.email,
                        userProperties,
                      );

                      // Exclusive event for GTM since it doesn't support `identify` method.
                      // [Tech Debt] Send same properties than `identify` method
                      // https://www.notion.so/filadd/Unify-USER_IDENTIFIED_EVENT-ea4457f382c84361befac531f4d25cda
                      window.rudderanalytics?.track("USER_IDENTIFIED_EVENT", {
                        userEmail: userData.email,
                        fullname: userData.fullname,
                        userId: userData.id,
                        userName: userData.name,
                        user_short_slug: userData.short_slug,
                        url_avatar: userData.url_avatar,
                        phone_number: userData.phone_number,
                      });
                    }
                  }}
                >
                  {`
                            !function(){var e=window.rudderanalytics=window.rudderanalytics||[];e.methods=["load","page","track","identify","alias","group","ready","reset","getAnonymousId","setAnonymousId"],e.factory=function(t){return function(){var r=Array.prototype.slice.call(arguments);return r.unshift(t),e.push(r),e}};for(var t=0;t<e.methods.length;t++){var r=e.methods[t];e[r]=e.factory(r)}e.loadJS=function(e,t){var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://static.filadd.com/public/checkout/ra.min.js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a)},e.loadJS(),
                            e.load(
                              "${RUDDERSTACK_KEY}",
                              "${RUDDERSTACKDATAURL}"),
                            e.ready(() => {
                              console.log("Rudderstack loaded")
                            })
                            e.page()
                            }();
                          `}
                </Script>
                <Toaster />

                {courseSlug && (
                  <BreadcrumbProvider
                    crumb={{
                      label: courseData?.short_title ?? "",
                      path: `/courses/${courseSlug}/content`,
                    }}
                  >
                    <Layout ssrCookies={cookies} courseSlug={courseSlug}>
                      <Component {...pageProps} />
                    </Layout>
                  </BreadcrumbProvider>
                )}

                {!courseSlug && <Component {...pageProps} />}
              </HydrationBoundary>
            </HydrationBoundary>
            <ReactQueryDevtools />
          </QueryClientProvider>
        </AppServiceWorkerProvider>
      </AuthProvider>
    </React.StrictMode>
  );
};

Application.getInitialProps = async (appContext: CustomAppContext) => {
  const loginUrl = getServerSideLoginUrl(appContext);
  const logoutUrl = getServerSideLogoutUrl(appContext);
  const courseSlug = appContext.ctx.query.courseSlug as string | undefined;

  let authorizationToken: string | undefined;

  if (appContext.ctx && appContext.ctx.req && appContext.ctx.req.cookies) {
    authorizationToken = appContext.ctx.req?.cookies["auth._token.local"];
  }

  const { req, res, locale } = appContext.ctx;
  const handler = nc()
    .use(cookieParser())
    .use(
      authorizationMiddleware(
        transcodeLocale(locale || "UNKNOWN", "icu", "iso3166"),
      ),
    );

  if (req && res) {
    try {
      await handler.run(req, res);
    } catch (e) {
      // TODO: Redirect user if any of the middlewares raised an exception?
      console.log(e);
    }
  }

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        meta: {
          baseURL: process.env.GATEWAY_URL_SERVER as string,
          authToken: authorizationToken,
          locale,
        },
      },
    },
  });

  const appProps = await App.getInitialProps(appContext);
  const userData = req?.userData;
  let courseData;
  const promises = [];

  if (courseSlug) {
    courseData = await useCourse.fetchQuery(queryClient, {
      ctx: appContext.ctx,
      params: { field: "slug", value: courseSlug },
    });

    promises.push(
      useLayoutFeatures.fetchQuery(queryClient, {
        courseSlug,
        ctx: appContext.ctx,
      }),
    );
    promises.push(
      queryClient.fetchQuery({
        queryKey: generateLayoutTrinketsKey(courseSlug),
        queryFn: fetchLayoutTrinkets,
        meta: { ssrContext: appContext.ctx },
      }),
    );
  }

  if (courseSlug && userData) {
    promises.push(NotificationsDock.prefetchQueries(queryClient));
  }

  await Promise.all(promises);

  return {
    ...appProps,
    loginUrl,
    logoutUrl,
    userData,
    courseData,
    courseSlug,
    authorizationToken,
    cookies: req?.cookies,
    initialDehydratedState: customDehydrate(queryClient),
  };
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default appWithTranslation(Application);
